import React, { useEffect, useState } from "react";
import { api, API } from "@/lib/api";
import { inr } from "@/lib/format";
import { SectionHeader, KPI } from "@/components/ui-extras";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { ClipboardList, Camera, Plus, Trash2, Users2, Wallet, Clock, Upload, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const FACADE_TRADES = [
  "Glazier", "Aluminium Fabricator", "Sealant Applicator", "Facade Installer",
  "Structural Glazing Fitter", "Curtain Wall Installer", "ACP Cladder",
  "Louver Fitter", "CNC Operator", "Anodizing / Powder Coat",
];

export default function Timesheet() {
  const [projects, setProjects] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [form, setForm] = useState({
    project_id: "", date: new Date().toISOString().slice(0, 10),
    trade: "Glazier", supervisor: "", default_monthly_salary: 26000, reason: "Extended shift",
  });
  const [rows, setRows] = useState([
    { worker_name: "", ot_hours: 2, employee_id: "" },
  ]);
  const [proofPath, setProofPath] = useState("");
  const [proofPreview, setProofPreview] = useState(null);
  const [proofBusy, setProofBusy] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      const [p, e] = await Promise.all([api.get("/projects"), api.get("/employees")]);
      setProjects(p.data); setEmployees(e.data);
      if (p.data.length && !form.project_id) setForm((f) => ({ ...f, project_id: p.data[0].project_id }));
    })();
  }, []);

  const addRow = () => setRows([...rows, { worker_name: "", ot_hours: 2, employee_id: "" }]);
  const removeRow = (i) => setRows(rows.filter((_, j) => j !== i));
  const updateRow = (i, k, v) => setRows(rows.map((r, j) => j === i ? { ...r, [k]: v } : r));
  const pickEmployee = (i, emp_id) => {
    const emp = employees.find((e) => e.employee_id === emp_id);
    if (!emp) return;
    updateRow(i, "employee_id", emp_id);
    updateRow(i, "worker_name", emp.name);
    updateRow(i, "monthly_salary", Math.round(Number(emp.ctc || 0) / 12));
  };

  const totalHours = rows.reduce((s, r) => s + Number(r.ot_hours || 0), 0);
  const perHour = form.default_monthly_salary ? form.default_monthly_salary / 26 / 8 : 0;
  const otHourly = perHour * 1.25;
  const estPay = rows.reduce((s, r) => {
    const mo = Number(r.monthly_salary || form.default_monthly_salary || 0);
    const ph = mo ? (mo / 26 / 8) * 1.25 : 0;
    return s + ph * Number(r.ot_hours || 0);
  }, 0);

  const uploadProof = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!/^image\/(jpeg|jpg|png|webp)$|^application\/pdf$/i.test(file.type)) {
      return toast.error("Upload JPG · PNG · WEBP · PDF");
    }
    const reader = new FileReader();
    reader.onload = async () => {
      const dataUrl = reader.result;
      setProofPreview(dataUrl);
      const b64 = dataUrl.split(",")[1];
      setProofBusy(true);
      try {
        const r = await api.post("/timesheet/proof", { image_base64: b64, mime_type: file.type });
        setProofPath(r.data.path);
        toast.success("OT slip photo uploaded");
      } catch { toast.error("Upload failed"); }
      finally { setProofBusy(false); }
    };
    reader.readAsDataURL(file);
  };

  const submit = async () => {
    if (!form.project_id) return toast.error("Select project");
    const validRows = rows.filter((r) => (r.worker_name || "").trim() && Number(r.ot_hours || 0) > 0);
    if (!validRows.length) return toast.error("Add at least one worker with OT hours");
    setSaving(true);
    try {
      const r = await api.post("/timesheet/bulk-ot", {
        ...form, default_monthly_salary: Number(form.default_monthly_salary || 0),
        proof_image_path: proofPath,
        rows: validRows.map((r) => ({
          worker_name: r.worker_name.trim(),
          employee_id: r.employee_id || "",
          ot_hours: Number(r.ot_hours || 0),
          monthly_salary: Number(r.monthly_salary || form.default_monthly_salary || 0),
          mode: "auto",
        })),
      });
      toast.success(`Saved ${r.data.saved} OT entries · ${inr(r.data.total_pay, { compact: true })}`);
      setRows([{ worker_name: "", ot_hours: 2, employee_id: "" }]);
      setProofPreview(null); setProofPath("");
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-4 max-w-3xl mx-auto" data-testid="timesheet-root">
      <SectionHeader
        title="Digital Timesheet"
        subtitle="Mobile-friendly OT punch-in for the whole gang · photo proof of the physical slip"
      />

      <div className="grid grid-cols-3 gap-3">
        <KPI testId="ts-workers" label="Workers" value={rows.filter((r) => r.worker_name).length} icon={Users2} />
        <KPI testId="ts-hours" label="Total OT hrs" value={totalHours} suffix=" hrs" icon={Clock} />
        <KPI testId="ts-est-pay" label="Est. OT pay" value={estPay} compact icon={Wallet} tone={estPay > 0 ? "warn" : "muted"} />
      </div>

      <Card className="shadow-none">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold font-display">Shift details</CardTitle>
        </CardHeader>
        <CardContent className="pt-0 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs text-muted-foreground mb-1 block">Project</Label>
            <Select value={form.project_id} onValueChange={(v) => setForm({ ...form, project_id: v })}>
              <SelectTrigger data-testid="ts-project"><SelectValue placeholder="Select project" /></SelectTrigger>
              <SelectContent>
                {projects.map((p) => (
                  <SelectItem key={p.project_id} value={p.project_id}>
                    <span className="font-mono text-xs mr-2 text-muted-foreground">{p.code}</span>{p.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-muted-foreground mb-1 block">Date</Label>
            <Input data-testid="ts-date" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
          </div>
          <div>
            <Label className="text-xs text-muted-foreground mb-1 block">Gang trade</Label>
            <Select value={form.trade} onValueChange={(v) => setForm({ ...form, trade: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{FACADE_TRADES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-muted-foreground mb-1 block">Supervisor</Label>
            <Input data-testid="ts-supervisor" value={form.supervisor} onChange={(e) => setForm({ ...form, supervisor: e.target.value })} placeholder="e.g. Ravi (Site)" />
          </div>
          <div>
            <Label className="text-xs text-muted-foreground mb-1 block">Default monthly salary (₹)</Label>
            <Input data-testid="ts-default-salary" type="number" value={form.default_monthly_salary}
              onChange={(e) => setForm({ ...form, default_monthly_salary: e.target.value })} />
            <div className="text-[10px] text-muted-foreground mt-1 font-mono">
              per hour ≈ {inr(perHour)} · OT/hr (1.25×) ≈ {inr(otHourly)}
            </div>
          </div>
          <div>
            <Label className="text-xs text-muted-foreground mb-1 block">Reason (applies to all)</Label>
            <Input data-testid="ts-reason" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} />
          </div>
        </CardContent>
      </Card>

      <Card className="shadow-none">
        <CardHeader className="pb-2 flex-row items-center justify-between">
          <CardTitle className="text-sm font-semibold font-display">Punch overtime · gang</CardTitle>
          <Button size="sm" data-testid="ts-add-row" onClick={addRow} className="h-8 bg-primary hover:bg-primary/90">
            <Plus className="h-3 w-3 mr-1" /> Add worker
          </Button>
        </CardHeader>
        <CardContent className="pt-0 space-y-2">
          {rows.map((r, i) => (
            <div key={i} data-testid={`ts-row-${i}`} className="grid grid-cols-12 gap-2 items-end border rounded-md p-2 bg-muted/20">
              <div className="col-span-12 sm:col-span-4">
                <Label className="text-[10px] uppercase font-mono text-muted-foreground mb-1 block">Worker</Label>
                <Input value={r.worker_name} onChange={(e) => updateRow(i, "worker_name", e.target.value)}
                  placeholder="Type or pick from employees" data-testid={`ts-worker-${i}`} className="h-9" />
              </div>
              <div className="col-span-8 sm:col-span-3">
                <Label className="text-[10px] uppercase font-mono text-muted-foreground mb-1 block">Link employee (optional)</Label>
                <Select value={r.employee_id || ""} onValueChange={(v) => pickEmployee(i, v)}>
                  <SelectTrigger className="h-9"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    {employees.map((e) => <SelectItem key={e.employee_id} value={e.employee_id}>{e.emp_code} · {e.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-4 sm:col-span-2">
                <Label className="text-[10px] uppercase font-mono text-muted-foreground mb-1 block">OT hrs</Label>
                <Input type="number" step="0.25" value={r.ot_hours} onChange={(e) => updateRow(i, "ot_hours", e.target.value)}
                  data-testid={`ts-hours-${i}`} className="h-9 text-right" />
              </div>
              <div className="col-span-8 sm:col-span-2">
                <Label className="text-[10px] uppercase font-mono text-muted-foreground mb-1 block">Salary/mo (override)</Label>
                <Input type="number" value={r.monthly_salary || ""} onChange={(e) => updateRow(i, "monthly_salary", e.target.value)}
                  placeholder={String(form.default_monthly_salary)} className="h-9 text-right" />
              </div>
              <div className="col-span-4 sm:col-span-1 flex justify-end">
                <Button size="sm" variant="ghost" data-testid={`ts-remove-${i}`}
                  onClick={() => removeRow(i)} className="h-9 w-9 p-0 text-destructive">
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          ))}
          {rows.length === 0 && (
            <div className="text-sm text-muted-foreground text-center py-6">Add at least one worker</div>
          )}
        </CardContent>
      </Card>

      <Card className="shadow-none">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold font-display flex items-center gap-2">
            <Camera className="h-4 w-4 text-secondary" /> OT slip photo proof
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0 space-y-2">
          <input type="file" accept="image/jpeg,image/png,image/webp,application/pdf"
            onChange={uploadProof} data-testid="ts-proof-file"
            className="block w-full text-xs border rounded-md p-2 file:mr-2 file:rounded file:border-0 file:bg-primary file:text-primary-foreground file:px-3 file:py-1" />
          {proofBusy && <div className="text-xs text-muted-foreground flex items-center gap-1"><Upload className="h-3 w-3 animate-pulse" /> Uploading…</div>}
          {proofPreview && (
            <div className="border rounded p-2 bg-muted/40">
              {proofPreview.startsWith("data:application/pdf") ? (
                <div className="text-xs text-muted-foreground font-mono p-3 text-center">📄 PDF uploaded</div>
              ) : (
                <img src={proofPreview} alt="OT slip" className="max-h-40 mx-auto object-contain rounded" />
              )}
              {proofPath && (
                <div className="mt-2 text-[10px] text-emerald-700 flex items-center gap-1 font-mono truncate">
                  <CheckCircle2 className="h-3 w-3" /> Attached · will be linked to every OT entry below
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="shadow-none border-primary/30 bg-primary/5 sticky bottom-4">
        <CardContent className="p-3 flex items-center justify-between gap-3">
          <div className="text-xs">
            <div className="text-muted-foreground uppercase font-mono tracking-wide text-[10px]">Estimated total OT pay</div>
            <div className="text-lg font-bold font-display text-primary">{inr(estPay, { compact: true })}</div>
            <div className="text-[10px] text-muted-foreground">{rows.filter((r) => r.worker_name).length} workers · {totalHours} hrs</div>
          </div>
          <Button data-testid="ts-submit" onClick={submit} disabled={saving}
            className="h-11 px-6 bg-primary hover:bg-primary/90">
            <ClipboardList className="h-4 w-4 mr-2" />
            {saving ? "Saving…" : "Punch OT for the gang"}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
